import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getArticleBySlug } from '@/mocks/articles';
import { mainSiteUrl } from '@/mocks/categories';
import FooterSection from '@/pages/home/components/FooterSection';
import Navbar from '@/components/Navbar';

const siteUrl = 'https://basetaosheet.mom';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = slug ? getArticleBySlug(slug) : undefined;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center bg-background-50 pt-20">
          <div className="text-center px-4">
            <h1 className="text-2xl font-heading font-semibold text-foreground-900 mb-3">Article Not Found</h1>
            <p className="text-foreground-600 mb-6">The article you are looking for does not exist.</p>
            <Link
              to="/"
              className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 transition-colors duration-200 cursor-pointer"
            >
              <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
              Back to Home
            </Link>
          </div>
        </main>
      </>
    );
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.metaDescription,
    datePublished: article.publishedDate,
    image: article.headerImage,
    author: {
      '@type': 'Organization',
      name: 'BaseTao Spreadsheet Guide',
    },
    publisher: {
      '@type': 'Organization',
      name: 'BaseTao Spreadsheet Guide',
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/0dc5bc4081b05862da3d01553c9c3d9.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteUrl}/article/${article.slug}`,
    },
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: article.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: `${siteUrl}/`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Shopping Guide',
        item: `${siteUrl}/guides`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
        item: `${siteUrl}/article/${article.slug}`,
      },
    ],
  };

  return (
    <>
      <Navbar />
      <main>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
        />

        <article className="bg-background-50">
          <header className="relative w-full py-16 md:py-24 overflow-hidden">
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{
                backgroundImage: `url('${article.headerImage}')`,
              }}
            />
            <div className="absolute inset-0 bg-black/40" />

            <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-6 text-center pt-8">
              <div className="flex items-center justify-center gap-3 mb-4">
                <span className="px-3 py-1 rounded-full bg-accent-100/80 text-accent-800 text-xs font-semibold tracking-wide">
                  {article.tag}
                </span>
                <span className="text-white/70 text-xs">{article.publishedDate}</span>
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white mb-4 leading-tight">
                {article.title}
              </h1>
              <p className="text-white/80 text-base md:text-lg max-w-2xl mx-auto">
                {article.metaDescription}
              </p>
            </div>
          </header>

          <div className="max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-10" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-foreground-700 transition-colors duration-200">
                Home
              </Link>
              <span className="text-foreground-400">/</span>
              <Link to="/guides" className="hover:text-foreground-700 transition-colors duration-200">
                Shopping Guide
              </Link>
              <span className="text-foreground-400">/</span>
              <span className="text-foreground-700 font-medium truncate">{article.title}</span>
            </nav>

            <div className="space-y-10">
              {article.sections.map((section, index) => (
                <section key={index}>
                  <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-4">
                    {section.heading}
                  </h2>
                  <div className="text-foreground-700 leading-relaxed text-base space-y-4">
                    {section.content.split('\n').filter(Boolean).map((paragraph, pIndex) => (
                      <p key={pIndex}>{paragraph}</p>
                    ))}
                  </div>
                </section>
              ))}
            </div>

            <div className="mt-12 p-6 md:p-8 rounded-xl bg-background-100 border border-background-200/70">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-1">
                    Want to start shopping?
                  </h3>
                  <p className="text-foreground-600 text-sm">
                    Visit the main platform to explore products and apply everything you have learned.
                  </p>
                </div>
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-colors duration-200 cursor-pointer flex-shrink-0"
                >
                  BaseTao Spreadsheet
                  <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
                </a>
              </div>
            </div>

            <section className="mt-14">
              <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-6">
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                {article.faqs.map((faq, index) => (
                  <details
                    key={index}
                    className="group rounded-xl bg-background-100 border border-background-200/70 overflow-hidden"
                  >
                    <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                      <h3 className="text-base font-semibold text-foreground-900 pr-4">
                        {faq.question}
                      </h3>
                      <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-background-200 group-open:bg-primary-100 transition-colors duration-200">
                        <i className="ri-add-line w-4 h-4 flex items-center justify-center text-foreground-600 group-open:hidden"></i>
                        <i className="ri-subtract-line w-4 h-4 hidden group-open:flex items-center justify-center text-primary-600"></i>
                      </div>
                    </summary>
                    <div className="px-5 pb-5">
                      <p className="text-foreground-700 text-sm leading-relaxed">{faq.answer}</p>
                    </div>
                  </details>
                ))}
              </div>
            </section>

            <div className="mt-14 pt-10 border-t border-background-200/70">
              <p className="text-center text-foreground-500 text-sm mb-4">
                <i className="ri-arrow-left-line w-4 h-4 inline-flex items-center justify-center"></i>
                {' '}Return to homepage for more resources
              </p>
              <div className="flex justify-center">
                <Link
                  to="/"
                  className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-secondary-500 text-white font-medium text-sm hover:bg-secondary-600 transition-colors duration-200 cursor-pointer"
                >
                  <i className="ri-home-4-line w-4 h-4 flex items-center justify-center"></i>
                  Back to Homepage
                </Link>
              </div>
            </div>
          </div>
        </article>

        <FooterSection />
      </main>
    </>
  );
}